/**
 * Lazy-loads the Paystack Inline JS SDK and exposes a typed wrapper around
 * `PaystackPop.setup({...}).openIframe()`.
 *
 * Script: https://js.paystack.co/v1/inline.js
 *
 * This module is client-only; never import from server code.
 */

export type PaystackCustomField = {
  display_name: string;
  variable_name: string;
  value: string;
};

export type PaystackSetupArgs = {
  key: string; // public key
  email: string;
  amount: number; // in kobo/cents (smallest unit; XOF is also ×100)
  currency?: "ZAR" | "NGN" | "GHS" | "USD" | "KES" | "XOF";
  ref: string;
  label?: string; // shown as modal title
  metadata?: {
    custom_fields?: PaystackCustomField[];
    [key: string]: unknown;
  };
  callback: (response: { reference: string; status?: string }) => void;
  onClose: () => void;
};

type PaystackHandler = {
  openIframe: () => void;
};

type PaystackPopType = {
  setup: (args: PaystackSetupArgs) => PaystackHandler;
};

declare global {
  interface Window {
    PaystackPop?: PaystackPopType;
  }
}

const SCRIPT_URL = "https://js.paystack.co/v1/inline.js";
let _loadPromise: Promise<PaystackPopType> | null = null;

export function loadPaystack(): Promise<PaystackPopType> {
  if (typeof window === "undefined") {
    return Promise.reject(new Error("Paystack inline is browser-only"));
  }
  if (window.PaystackPop) return Promise.resolve(window.PaystackPop);
  if (_loadPromise) return _loadPromise;

  _loadPromise = new Promise<PaystackPopType>((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(
      `script[src="${SCRIPT_URL}"]`
    );
    if (existing) {
      existing.addEventListener("load", () => {
        if (window.PaystackPop) resolve(window.PaystackPop);
        else reject(new Error("Paystack failed to initialise"));
      });
      return;
    }
    const s = document.createElement("script");
    s.src = SCRIPT_URL;
    s.async = true;
    s.onload = () => {
      if (window.PaystackPop) resolve(window.PaystackPop);
      else reject(new Error("Paystack failed to initialise"));
    };
    s.onerror = () => reject(new Error("Couldn't load Paystack"));
    document.head.appendChild(s);
  });
  return _loadPromise;
}

/** One-shot helper that opens the popup and resolves on success/cancel. */
export type PaystackOutcome =
  | { kind: "success"; reference: string }
  | { kind: "cancelled" };

export async function openPaystack(
  args: Omit<PaystackSetupArgs, "callback" | "onClose">
): Promise<PaystackOutcome> {
  const PaystackPop = await loadPaystack();
  return new Promise<PaystackOutcome>((resolve) => {
    let settled = false;
    const settle = (v: PaystackOutcome) => {
      if (settled) return;
      settled = true;
      resolve(v);
    };
    const handler = PaystackPop.setup({
      ...args,
      callback: (response) => {
        settle({ kind: "success", reference: response.reference });
      },
      onClose: () => {
        settle({ kind: "cancelled" });
      },
    });
    handler.openIframe();
  });
}
